import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, withCtx as _withCtx, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "doc-diagram-bar flex flex-wrap items-center gap-2 mb-4 p-3 bg-muted/30 rounded-lg border border-border/50"
}
const _hoisted_2 = { class: "truncate max-w-[16rem]" }
const _hoisted_3 = { class: "prose prose-sm dark:prose-invert max-w-none" }

import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { MdPreview } from 'md-editor-v3'
import 'md-editor-v3/lib/preview.css'
import { Workflow } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import MermaidLivePreview from './MermaidLivePreview.vue'

// Vista de un documento markdown con soporte de diagramas Mermaid:
// - barra superior con los diagramas detectados
// - botón "Abrir visor" sobre cada diagrama renderizado (aparece al ir bajando)
// - visor avanzado (zoom, pan, pantalla completa, exportar) en un diálogo
const OPEN_BTN_CLASS = 'doc-mermaid-open'
const OPEN_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h6v6"/><path d="M9 21H3v-6"/><path d="M21 3l-7 7"/><path d="M3 21l7-7"/></svg>`

// md-editor-v3 renderiza cada diagrama como `p.md-editor-mermaid[data-content]`
// y le agrega su propia barra `.md-editor-mermaid-action` (copiar). Ahí sumamos
// nuestro botón. md-editor re-renderiza el HTML al cambiar el contenido, por eso
// se vuelve a inyectar con un MutationObserver.

export default /*@__PURE__*/_defineComponent({
  __name: 'DocumentMarkdownView',
  props: {
    content: {},
    showDiagramBar: { type: Boolean, default: true }
  },
  setup(__props: any) {

const props = __props

const rootRef = ref<HTMLElement | null>(null)
const showDiagramViewer = ref(false)
const diagramCode = ref('')

// El título se define con una línea `%% title: <nombre>` dentro del bloque
const DIAGRAM_TITLE_RE = /^%%\s*title:\s*(.+)$/m

const mermaidDiagrams = computed(() =>
  Array.from((props.content || '').matchAll(/```mermaid\r?\n([\s\S]*?)```/g)).map(m => {
    const code = m[1].trim()
    const titleMatch = code.match(DIAGRAM_TITLE_RE)
    return { code, title: titleMatch ? titleMatch[1].trim() : '' }
  })
)

const openDiagram = (code: string) => {
  diagramCode.value = code
  showDiagramViewer.value = true
}

const injectOpenButtons = () => {
  const root = rootRef.value
  if (!root) return
  root.querySelectorAll<HTMLElement>('.md-editor-preview .md-editor-mermaid[data-processed]').forEach((diagram) => {
    if (diagram.querySelector(`.${OPEN_BTN_CLASS}`)) return
    let actions = diagram.querySelector<HTMLElement>('.md-editor-mermaid-action')
    if (!actions) {
      actions = document.createElement('div')
      actions.className = 'md-editor-mermaid-action'
      diagram.appendChild(actions)
    }
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = OPEN_BTN_CLASS
    btn.title = 'Abrir en visor avanzado (zoom y pantalla completa)'
    btn.innerHTML = `${OPEN_ICON}<span>Abrir visor</span>`
    btn.addEventListener('click', (event) => {
      event.preventDefault()
      event.stopPropagation()
      const code = diagram.dataset.content
      if (code) openDiagram(code)
    })
    actions.prepend(btn)
  })
}

let observer: MutationObserver | null = null

onMounted(() => {
  if (!rootRef.value) return
  observer = new MutationObserver(() => injectOpenButtons())
  observer.observe(rootRef.value, { childList: true, subtree: true })
  injectOpenButtons()
})

onBeforeUnmount(() => {
  observer?.disconnect()
  observer = null
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "rootRef",
    ref: rootRef,
    class: "document-markdown-view"
  }, [
    (__props.showDiagramBar && mermaidDiagrams.value.length > 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _cache[1] || (_cache[1] = _createElementVNode("span", { class: "text-xs font-medium text-muted-foreground mr-1" }, " Diagramas detectados: ", -1)),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(mermaidDiagrams.value, (diagram, index) => {
            return (_openBlock(), _createBlock(_unref(Button), {
              key: index,
              variant: "outline",
              size: "sm",
              class: "h-7 text-xs text-primary hover:text-primary border-primary/20 hover:border-primary/50 bg-primary/5",
              title: diagram.title || undefined,
              onClick: ($event: any) => (openDiagram(diagram.code))
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(Workflow), { class: "w-3.5 h-3.5 mr-1.5 shrink-0" }),
                _createElementVNode("span", _hoisted_2, _toDisplayString(diagram.title || ('Ver Diagrama ' + (mermaidDiagrams.value.length > 1 ? index + 1 : ''))), 1)
              ]),
              _: 2
            }, 1032, ["title", "onClick"]))
          }), 128))
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_3, [
      _createVNode(_unref(MdPreview), {
        "model-value": __props.content,
        language: "en-US"
      }, null, 8, ["model-value"])
    ]),
    _createVNode(_unref(Dialog), {
      open: showDiagramViewer.value,
      "onUpdate:open": _cache[0] || (_cache[0] = ($event: any) => ((showDiagramViewer).value = $event))
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(DialogContent), { class: "z-[200] max-w-6xl w-[90vw] h-[85vh] p-0 flex flex-col overflow-hidden bg-background" }, {
          default: _withCtx(() => [
            _createVNode(MermaidLivePreview, { code: diagramCode.value }, null, 8, ["code"])
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["open"])
  ], 512))
}
}

})